import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useCallback,
} from 'react';
import {
  InfoCircleFilled,
  QuestionCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import find from 'lodash/find';
import { Divider } from 'antd';
import { RadioGroup, Select } from 'components/FormFields';
import { FormItem } from 'components/Form';
import Popover from 'components/Popover';
import { getCurrencyValue } from 'utils/number';
import { scrollToErrorField, validateForm } from 'utils/form';
import InputItem from 'views/pages/ClientDetail/components/InputItem';
import { FormSection } from 'views/pages/ClientDetail/components/Elements';
import NewCardForm from './NewCardForm';
import NewAccountForm from './NewAccountForm';
import classes from '../../NewOrderModal.module.scss';
import { paymentValidateObj, ACCOUNTS_LIST, CARDS_LIST } from '../../utils';
import { PAYMENT_METHODS } from '../../constants';
import { useNewOrderModalData } from '../../../../selectorData';
import { useWatch } from 'antd/es/form/Form';

const PaymentsStep = forwardRef(
  (
    {
      form,
      style,
      getBankName,
      currentStep,
      permission,
      cardError,
      cartOrders = [],
      onPricingChange,
    },
    ref
  ) => {
    const formPaymentMethod = form.getFieldValue('payment_method');
    const [paymentMethod, setPaymentMethod] = useState(formPaymentMethod);
    const {
      accounts,
      cards,
      orderTypeDetail,
      orderTypes,
    } = useNewOrderModalData();
    const [isPaymentNeeded, setIsPaymentNeeded] = useState(false);

    const price = useWatch('price', form);
    const rushOrder = useWatch('rush_order', form);

    // Calculate if payment is required based on total amount (current + all cart orders).
    // Cart order fields are stored as cart_price_N / cart_rush_order_N in the form.
    const isPaymentRequired = useCallback(
      (values = null) => {
        const formValues = values || form.getFieldsValue();
        const orderPrice = Number(formValues.price) || 0;
        const rushAmount = !!formValues.rush_order?.length
          ? orderTypeDetail.rush_amount || 0
          : 0;
        const cartTotal = cartOrders.reduce((sum, co, idx) => {
          const cp =
            Number(formValues[`cart_price_${idx}`] ?? co.formValues.price) || 0;
          const rushChecked =
            formValues[`cart_rush_order_${idx}`]?.length ??
            co.formValues.rush_order?.length;
          const cr = rushChecked ? co.orderTypeDetail.rush_amount || 0 : 0;
          return sum + cp + cr;
        }, 0);
        return orderPrice + rushAmount + cartTotal > 0;
      },
      [form, orderTypeDetail, cartOrders]
    );

    useEffect(() => {
      const values = form.getFieldsValue();
      setIsPaymentNeeded(isPaymentRequired(values));
    }, [form, isPaymentRequired, price, rushOrder]);

    const paymentMethodList = tPaymentMethod => {
      const paymentMethods =
        tPaymentMethod === 'account'
          ? ACCOUNTS_LIST(accounts)
          : CARDS_LIST(cards);
      paymentMethods.push({
        label: (
          <>
            Add a New Account <PlusCircleOutlined />
          </>
        ),
        value: 0,
      });
      return paymentMethods;
    };
    useImperativeHandle(ref, () => ({
      validateStep(cb) {
        const values = form.getFieldsValue();
        const paymentRequired = isPaymentRequired(values);

        // If payment is not required (total = 0), skip payment validation
        const fieldErrors = validateForm(
          paymentValidateObj(values, permission, paymentRequired),
          values,
          true
        );
        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          scrollToErrorField();
        } else {
          cb(true);
        }
      },
    }));

    useEffect(() => {
      const values = form.getFieldsValue();
      const paymentRequired = isPaymentRequired(values);
      const formPaymentMethodId = values['payment_method_id'];

      if (
        paymentRequired &&
        (formPaymentMethodId === null || formPaymentMethodId === undefined)
      ) {
        const initialPaymentMethod = paymentMethodList(paymentMethod);
        if (initialPaymentMethod.length) {
          form.setFieldsValue({
            payment_method_id: initialPaymentMethod[0].value,
          });
        }
      }

      const previousPrice = values?.price;
      if (currentStep === 2 && orderTypeDetail.price && !previousPrice) {
        form.setFieldsValue({
          price: orderTypeDetail.price,
        });
      }

      // Pre-populate editable cart order fields (cart_price_N, cart_rush_order_N)
      // so each cart row shows the saved values and the user can adjust them.
      if (currentStep === 2 && cartOrders.length > 0) {
        const cartFieldValues = {};
        cartOrders.forEach((co, idx) => {
          cartFieldValues[`cart_price_${idx}`] = co.formValues.price;
          cartFieldValues[`cart_rush_order_${idx}`] = co.formValues.rush_order;
        });
        form.setFieldsValue(cartFieldValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);
    const cartFieldValues = useWatch([], form);
    // update the cart sidebar pricing whenever any related field changes (cart_price_N, cart_rush_order_N, price, rush_order)
    useEffect(() => {
      if (!onPricingChange) return;

      const values = cartFieldValues || {};
      // Cart items
      const items = cartOrders.map((co, idx) => {
        const price =
          Number(values[`cart_price_${idx}`] ?? co.formValues.price) || 0;

        const rushChecked =
          values[`cart_rush_order_${idx}`]?.length ??
          co.formValues.rush_order?.length;

        const rushAmount = rushChecked
          ? co.orderTypeDetail.rush_amount || 0
          : 0;

        return {
          ...co,
          computedPrice: price + rushAmount,
        };
      });

      // ✅ Current order
      const currentPrice = Number(values.price) || 0;
      const currentRush = values.rush_order?.length
        ? orderTypeDetail.rush_amount || 0
        : 0;

      // ✅ Grand total
      const grandTotal =
        items.reduce((sum, item) => sum + item.computedPrice, 0) +
        currentPrice +
        currentRush;

      onPricingChange({
        items,
        grandTotal,
      });
    }, [cartFieldValues, cartOrders, onPricingChange, orderTypeDetail]);

    return (
      <div
        style={style}
        className={cn(classes.stepContent, classes.paymentWrapper)}
      >
        <div className={classes.paymentInnerContent}>
          {/* ── ROW 1: Price details card, full width, one row per order ── */}
          <div className={classes.paymentOverview}>
            <div className={classes.paymentOverviewInnerContent}>
              {/* Cart orders — editable price input + enabled rush checkbox per row */}
              {cartOrders.map((cartOrder, idx) => {
                const cartRushAmt = cartOrder.orderTypeDetail.rush_amount || 0;
                return (
                  <div key={cartOrder.id} className={classes.orderPriceRow}>
                    <div className={classes.subtotal}>
                      <label className={classes.orderRowLabel}>
                        {idx + 1}. {cartOrder.orderTypeName}
                      </label>
                      <InputItem
                        name={`cart_price_${idx}`}
                        className={classes.orderPriceInput}
                        label=""
                        type="price"
                        prefix=" "
                        disabled={!permission.editOrderPrice}
                        form={form}
                        placeholder="Order Price"
                      />
                    </div>
                    <div className={classes.rushOrder}>
                      <InputItem
                        name={`cart_rush_order_${idx}`}
                        className={classes.rushOrderInput}
                        type={'checkbox'}
                        label={
                          <>
                            Rush Order (+ $
                            {cartRushAmt ? cartRushAmt.toFixed(2) : '0.00'})
                          </>
                        }
                      />
                      <Popover
                        placement={'bottomLeft'}
                        overlayClassName={classes.rushOrderInfo}
                        content={
                          <>
                            <InfoCircleFilled />
                            <p>
                              We start all orders within 24 hours. If you would
                              like us to start on your order within an hour
                              please select the Rush Order option.
                            </p>
                          </>
                        }
                        trigger={'hover'}
                      >
                        <QuestionCircleOutlined
                          className={classes.infoIcon}
                          size={'large'}
                        />
                      </Popover>
                    </div>
                  </div>
                );
              })}

              {/* Current (active form) order — only shown when user has selected an order type */}
              <FormItem shouldUpdate noMargin>
                {() => {
                  const values = form.getFieldsValue();
                  const currentOrderType = find(orderTypes, {
                    value: values.order_type_id,
                  });
                  if (!currentOrderType) return null;
                  return (
                    <>
                      {cartOrders.length > 0 && (
                        <Divider style={{ margin: '12px 0' }} />
                      )}
                      <div className={classes.orderPriceRow}>
                        <div className={classes.subtotal}>
                          <label className={classes.orderRowLabel}>
                            {cartOrders.length + 1}. {currentOrderType.label}
                          </label>
                          <InputItem
                            name="price"
                            className={classes.orderPriceInput}
                            label=""
                            type="price"
                            prefix=" "
                            disabled={!permission.editOrderPrice}
                            form={form}
                            placeholder="Order Price"
                          />
                        </div>
                        <div className={classes.rushOrder}>
                          <InputItem
                            name={'rush_order'}
                            className={classes.rushOrderInput}
                            type={'checkbox'}
                            label={
                              <>
                                Rush Order (+ $
                                {orderTypeDetail.rush_amount
                                  ? orderTypeDetail.rush_amount.toFixed(2)
                                  : '0.00'}
                                )
                              </>
                            }
                          />
                          <Popover
                            placement={'bottomLeft'}
                            overlayClassName={classes.rushOrderInfo}
                            content={
                              <>
                                <InfoCircleFilled />
                                <p>
                                  We start all orders within 24 hours. If you
                                  would like us to start on your order within an
                                  hour please select the Rush Order option.
                                </p>
                              </>
                            }
                            trigger={'hover'}
                          >
                            <QuestionCircleOutlined
                              className={classes.infoIcon}
                              size={'large'}
                            />
                          </Popover>
                        </div>
                      </div>
                    </>
                  );
                }}
              </FormItem>

              {/* Grand Total */}
              <div className={classes.total}>
                <span>{cartOrders.length > 0 ? 'Grand Total' : 'Total'}</span>
                <span>
                  <FormItem shouldUpdate>
                    {() => {
                      const values = form.getFieldsValue();
                      const orderPrice = Number(values.price) || 0;
                      const rushAmount = !!values.rush_order?.length
                        ? orderTypeDetail.rush_amount || 0
                        : 0;
                      // Read from live form fields (cart_price_N / cart_rush_order_N)
                      const cartTotal = cartOrders.reduce((sum, co, idx) => {
                        const cp =
                          Number(
                            values[`cart_price_${idx}`] ?? co.formValues.price
                          ) || 0;
                        const rushChecked =
                          values[`cart_rush_order_${idx}`]?.length ??
                          co.formValues.rush_order?.length;
                        const cr = rushChecked
                          ? co.orderTypeDetail.rush_amount || 0
                          : 0;
                        return sum + cp + cr;
                      }, 0);
                      return (
                        <>
                          {getCurrencyValue(
                            orderPrice + rushAmount + cartTotal
                          )}
                        </>
                      );
                    }}
                  </FormItem>
                </span>
              </div>
            </div>
          </div>

          {/* ── ROW 2: Payment method section ── */}
          {isPaymentNeeded ? (
            <div className={classes.paymentInputs}>
              <FormItem
                name="payment_method"
                label="Select your payment method."
                form={form}
              >
                <RadioGroup
                  name="payment_method"
                  items={PAYMENT_METHODS}
                  onChange={e => {
                    setPaymentMethod(e.target.value);
                    form.setFieldsValue({
                      payment_method: e.target.value,
                      payment_method_id:
                        paymentMethodList(e.target.value)[0]?.value || null,
                    });
                  }}
                  value={paymentMethod}
                />
              </FormItem>

              <FormItem
                className={classes.paymentMethod}
                name="payment_method_id"
                label="Payment Method"
                required={isPaymentNeeded}
              >
                <Select
                  name="payment_method_id"
                  placeholder="Payment Method"
                  form={form}
                  className={classes.paymentMethodDropDown}
                  dropdownClassName={classes.paymentMethodDropDown}
                  size="medium"
                  items={paymentMethodList(paymentMethod)}
                />
              </FormItem>
            </div>
          ) : (
            <div className={classes.noPaymentRequired}>
              <InfoCircleFilled
                style={{ color: '#1890ff', marginRight: '8px' }}
              />
              <span>No payment required for this order (Total: $0.00)</span>
            </div>
          )}
        </div>
        <FormItem shouldUpdate noMargin>
          {() => {
            const values = form.getFieldsValue();
            const paymentRequired = isPaymentRequired(values);
            const thisPaymentMethod = values['payment_method'];
            const thisPaymentMethodId = values['payment_method_id'];

            return (
              <>
                {paymentRequired && thisPaymentMethodId === 0 && (
                  <div className={classes.newPaymentMethodWrapper}>
                    <Divider />
                    {thisPaymentMethod === 'account' ? (
                      <NewAccountForm
                        getBankName={getBankName}
                        form={form}
                        index={'account'}
                      />
                    ) : (
                      <NewCardForm
                        form={form}
                        index={'card'}
                        cardError={cardError}
                        isRequired={paymentRequired}
                      />
                    )}
                  </div>
                )}
              </>
            );
          }}
        </FormItem>
        <Divider />
        <FormSection className={classes.noteField}>
          <div className="notesField100">
            <InputItem
              name={'notes'}
              label="Notes"
              form={form}
              fullWidth
              type="textarea"
            />
          </div>
        </FormSection>
      </div>
    );
  }
);

export default PaymentsStep;
